<template>
    <div
        class="preview-inline"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <slot></slot>
        <v-menu v-model="showMenu" :position-x="menuX" :position-y="menuY" absolute offset-y
            :close-on-content-click="false" max-width="800" min-width="700">
            <v-card @mouseenter="onMenuEnter" @mouseleave="onMenuLeave">
                <v-card-text class="pa-0 pb-2">
                    <div v-if="loading" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="20"></v-progress-circular>
                        <div class="text-caption mt-1">{{ $translate('loading') }}</div>
                    </div>
                    <div v-else-if="invoice && invoiceItems && invoiceItems.length > 0">
                        <div class="pa-4 pb-0">
                            <div class="d-flex justify-space-between align-center">
                                <div>
                                    <router-link :to="{
                                        name: 'customerinvoice',
                                        params: { invoice_id: invoice.id },
                                    }" class="text-subtitle-2 font-weight-bold text-decoration-none">
                                        {{ $utils.getInvoiceIdText(invoice) }}
                                    </router-link>
                                </div>
                                <div class="d-flex" style="gap: 4px; flex-wrap: wrap; justify-content: flex-end;">
                                    <v-chip x-small v-if="invoice.credit" color="primary" outlined>
                                        {{ $translate('credit') }}
                                    </v-chip>
                                    <v-chip x-small :color="$utils.getCustomerInvoiceStatusColor(invoice)"
                                        :outlined="$utils.getCustomerInvoiceStatusOutlined(invoice)">
                                        {{ $utils.getCustomerInvoiceStatusText(invoice) }}
                                    </v-chip>
                                    <v-chip x-small v-if="invoice.sent && invoice.status === 'validated'"
                                        :color="invoice.sent ? 'success' : 'grey'" outlined>
                                        <v-icon x-small class="mr-1">mdi-email-check</v-icon>
                                        {{ $translate('sent') }}
                                    </v-chip>
                                </div>
                            </div>
                            <div class="d-flex justify-space-between mt-2 mb-2">
                                <div class="text-caption grey--text">
                                    <div v-if="invoice.biller_data" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-domain</v-icon>
                                        {{ invoice.biller_data.name }}
                                    </div>
                                    <div v-if="invoice.billed_data" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-account</v-icon>
                                        {{ invoice.billed_data.name }}
                                    </div>
                                    <div v-if="invoice.invoice_date" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                        {{ $moment(invoice.invoice_date).format('ll') }}
                                    </div>
                                    <div v-if="invoice.due_date && !invoice.credit" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
                                        {{ $translate('due') }}: {{ $moment(invoice.due_date).format('ll') }}
                                    </div>
                                </div>
                                <div class="text-caption grey--text text-right">
                                    <div v-if="invoice.total_amount_excl_vat !== null" class="mb-1">
                                        <div class="font-weight-bold">{{ $utils.price(invoice.total_amount_excl_vat) }}
                                            {{ $translate('excl_vat') }}</div>
                                        <div class="text-caption">{{ $utils.price(invoice.total_amount_incl_vat) }} {{ $translate('incl_vat') }}
                                        </div>
                                    </div>
                                    <div class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-receipt</v-icon>
                                        {{ invoiceItems.length }} {{ $translate('lines') }}
                                    </div>
                                </div>
                            </div>
                            <v-divider class="mb-2"></v-divider>
                        </div>
                        <div class="invoice-items-list" style="max-height: 400px; overflow-y: auto; overflow-x: auto;">
                            <v-simple-table dense class="no-wrap-table">
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">{{ $translate('product') }}</th>
                                            <th class="text-right">{{ $translate('quantity') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in invoiceItems" :key="item.id" >
                                            <td class="text-left py-1">
                                                <div class="font-weight-bold">
                                                    {{ getItemLabel(item) }}
                                                    <i v-if="item.manufacturer" class="font-weight-bold grey--text">
                                                        - {{ item.manufacturer }}
                                                    </i>
                                                </div>
                                                <div v-if="item.description" class="text-caption grey--text">
                                                    {{ item.description }}
                                                </div>
                                            </td>
                                            <td class="text-right">
                                                <v-chip x-small outlined>
                                                    {{ $utils.formatSaleQuantityDisplay(item) }}
                                                </v-chip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </div>
                    </div>
                    <div v-else-if="invoice" class="pa-4">
                        <div class="d-flex justify-space-between align-center mb-3">
                            <div>
                                <router-link :to="{
                                    name: 'customerinvoice',
                                    params: { invoice_id: invoice.id },
                                }" class="text-subtitle-2 font-weight-bold text-decoration-none">
                                    {{ $utils.getInvoiceIdText(invoice) }}
                                </router-link>
                            </div>
                            <div class="d-flex" style="gap: 4px; flex-wrap: wrap; justify-content: flex-end;">
                                <v-chip x-small v-if="invoice.credit" color="primary" outlined>
                                    {{ $translate('credit') }}
                                </v-chip>
                                <v-chip x-small :color="$utils.getCustomerInvoiceStatusColor(invoice)"
                                    :outlined="$utils.getCustomerInvoiceStatusOutlined(invoice)">
                                    {{ $utils.getCustomerInvoiceStatusText(invoice) }}
                                </v-chip>
                                <v-chip x-small v-if="invoice.sent && invoice.status === 'validated'"
                                    :color="invoice.sent ? 'success' : 'grey'" outlined>
                                    <v-icon x-small class="mr-1">mdi-email-check</v-icon>
                                    {{ $translate('sent') }}
                                </v-chip>
                            </div>
                        </div>
                        <div class="text-caption grey--text">
                            <div v-if="invoice.biller_data" class="mb-1">
                                <v-icon x-small class="mr-1">mdi-domain</v-icon>
                                {{ invoice.biller_data.name }}
                            </div>
                            <div v-if="invoice.billed_data" class="mb-1">
                                <v-icon x-small class="mr-1">mdi-account</v-icon>
                                {{ invoice.billed_data.name }}
                            </div>
                            <div v-if="invoice.invoice_date" class="mb-1">
                                <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                {{ $moment(invoice.invoice_date).format('ll') }}
                            </div>
                            <div v-if="invoice.due_date && !invoice.credit" class="mb-1">
                                <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
                                {{ $translate('due') }}: {{ $moment(invoice.due_date).format('ll') }}
                            </div>
                            <div v-if="invoice.total_amount_excl_vat !== null" class="mb-1">
                                <v-icon x-small class="mr-1">mdi-currency-eur</v-icon>
                                <div class="font-weight-bold">{{ $utils.price(invoice.total_amount_excl_vat) }} {{ $translate('excl_vat') }}</div>
                                <div class="text-caption">{{ $utils.price(invoice.total_amount_incl_vat) }} {{ $translate('incl_vat') }}</div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-body-2 pa-4 text-center grey--text">
                        {{ $translate('no_invoice_found') }}
                    </div>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";

export default {
    name: "CustomerInvoicePreview",
    mixins: [GlobalMixins],
    props: {
        invoiceId: {
            type: [Number, String],
            required: true,
        },
        delay: {
            type: Number,
            default: 1000,
        },
    },
    data() {
        return {
            invoice: null,
            invoiceItems: null,
            loading: false,
            hoverTimer: null,
            showMenu: false,
            menuX: 0,
            menuY: 0,
        };
    },
    methods: {
        onMouseEnter(event) {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
            }

            const rect = event.currentTarget.getBoundingClientRect();
            this.menuX = rect.left + 50;
            this.menuY = rect.bottom;

            this.hoverTimer = setTimeout(() => {
                this.showMenu = true;
                this.loadInvoice();
            }, this.delay);
        },
        onMouseLeave() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }

            this.hoverTimer = setTimeout(() => {
                this.showMenu = false;
            }, 200);
        },
        onMenuEnter() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
        },
        onMenuLeave() {
            this.showMenu = false;
        },
        async loadInvoice() {
            if (this.invoice || this.loading) {
                return;
            }

            this.loading = true;

            try {
                const [invoiceResponse, itemsResponse] = await Promise.all([
                    axios.get(`/api/customerinvoices/${this.invoiceId}/`),
                    axios.get(`/api/customerinvoiceitems/?invoice=${this.invoiceId}&limit=1000`)
                ]);
                this.invoice = invoiceResponse.data;
                this.invoiceItems = itemsResponse.data.results || [];
            } catch (error) {
                console.error('Error loading invoice:', error);
                this.invoice = null;
                this.invoiceItems = null;
            } finally {
                this.loading = false;
            }
        },
        getItemLabel(item) {
            return item.label || '-';
        },
    },
    beforeDestroy() {
        if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
        }
    },
    i18n: {
        messages: {
            en: {
                loading: "Loading...",
                no_invoice_found: "Unable to load invoice information",
                credit: "Credit",
                due: "Due",
                paid: "Paid",
                unpaid: "Unpaid",
                sent: "Sent",
                not_sent: "Not sent",
                product: "Product",
                quantity: "Quantity",
                unit_price: "Unit price",
                total: "Total",
                lines: "lines",
                excl_vat: "HT",
                incl_vat: "TTC",
            },
            fr: {
                loading: "Chargement...",
                no_invoice_found: "Impossible de charger les informations de la facture",
                credit: "Avoir",
                due: "Échéance",
                paid: "Payée",
                unpaid: "Impayée",
                sent: "Envoyée",
                not_sent: "Non envoyée",
                product: "Produit",
                quantity: "Quantité",
                unit_price: "Prix unitaire",
                total: "Total",
                lines: "lignes",
                excl_vat: "HT",
                incl_vat: "TTC",
            },
        },
    },
};
</script>

<style scoped>
.preview-inline {
    display: inline;
}

.invoice-items-list {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.invoice-items-list::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.invoice-items-list::-webkit-scrollbar-track {
    background: transparent;
}

.invoice-items-list::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.invoice-items-list::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
}

.no-wrap-table>>>td,
.no-wrap-table>>>th {
    white-space: nowrap;
}

.no-wrap-table>>>table {
    table-layout: auto;
}

.no-wrap-table>>>tbody tr:hover {
    background-color: transparent !important;
}
</style>

